import React, { useState, useEffect } from 'react';
import {
  Table, Tag, Badge, Button, Input, Select, Space, Drawer, Modal,
  Upload, message, Form, Row, Col, Typography, Card, Tooltip
} from 'antd';
import {
  TeamOutlined, PlusOutlined, UploadOutlined, SearchOutlined,
  MessageOutlined, WhatsAppOutlined
} from '@ant-design/icons';
import apiClient from '../api';
import './LeadList.css';

const { Title, Text } = Typography;
const { Option } = Select;
const { TextArea } = Input;

const STATUS_COLORS = {
  new: 'blue',
  contacted: 'orange',
  qualified: 'purple',
  converted: 'green',
  churned: 'default' // grey
};

const LeadList = ({ accountId, apiKey }) => {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(false);
  const [totalLeads, setTotalLeads] = useState(0);
  const [filters, setFilters] = useState({ page: 1, limit: 50 });
  const [stats, setStats] = useState({ total: 0, new: 0, contacted: 0, converted: 0 });

  // Drawer
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [selectedLead, setSelectedLead] = useState(null);

  // Modals
  const [addModalVisible, setAddModalVisible] = useState(false);
  const [importModalVisible, setImportModalVisible] = useState(false);

  const [addForm] = Form.useForm();
  const [importFile, setImportFile] = useState(null);

  useEffect(() => {
    fetchLeads();
  }, [filters, accountId]);

  const fetchLeads = async () => {
    setLoading(true);
    try {
      const res = await apiClient.get('/leads', {
        params: filters,
        headers: { 'x-api-key': apiKey }
      });
      console.log("🚀 ~ fetchLeads ~ res:", res.data)
      setLeads(res.data.leads || []);
      setTotalLeads(res.data.total || 0);

      // We should calculate stats ideally from a backend aggregation, but for simplicity we calculate from fetched data or just placeholders here
      // Alternatively we can fetch a /api/leads/stats endpoint.
      // Let's do a simple client side approx for now, or assume we only show stats of currently viewed page.
      const currentLeads = res.data.leads || [];
      setStats({
        total: res.data.total,
        new: currentLeads.filter(l => l.status === 'new').length,
        contacted: currentLeads.filter(l => l.status === 'contacted').length,
        converted: currentLeads.filter(l => l.status === 'converted').length,
      });

    } catch (err) {
      message.error('Failed to load leads');
    }
    setLoading(false);
  };

  const handleTableChange = (pagination) => {
    setFilters(prev => ({ ...prev, page: pagination.current, limit: pagination.pageSize }));
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value, page: 1 }));
  };

  const openDrawer = (record) => {
    setSelectedLead(record);
    setDrawerVisible(true);
  };

  const handleAddSubmit = async (values) => {
    try {
      await apiClient.post('/leads', values, { headers: { 'x-api-key': apiKey } });
      message.success('Lead added');
      setAddModalVisible(false);
      addForm.resetFields();
      fetchLeads();
    } catch (e) {
      message.error('Failed to add lead');
    }
  };

  const handleUpdateLead = async (updates) => {
    if (!selectedLead) return;
    try {
      const res = await apiClient.put(`/leads/${selectedLead._id}`, updates, { headers: { 'x-api-key': apiKey } });
      setSelectedLead(res.data);
      message.success('Lead updated');
      fetchLeads();
    } catch (e) {
      message.error('Failed to update lead');
    }
  };

  const handleImport = async () => {
    if (!importFile) return message.warning('Please select a CSV file');

    const formData = new FormData();
    formData.append('file', importFile);

    try {
      const res = await apiClient.post('/leads/import/csv', formData, {
        headers: { 'x-api-key': apiKey, 'Content-Type': 'multipart/form-data' }
      });
      message.success(`Imported ${res.data.imported} leads, skipped ${res.data.skipped} duplicates`);
      setImportModalVisible(false);
      setImportFile(null);
      fetchLeads();
    } catch (e) {
      message.error('Failed to import CSV');
    }
  };

  const columns = [
    {
      title: 'Name',
      dataIndex: 'name',
      render: (text, record) => (
        <Space>
          <a onClick={() => openDrawer(record)}>{text || 'Unknown'}</a>
          {record.phone && <Tooltip title="Has WhatsApp"><WhatsAppOutlined style={{ color: '#52c41a' }} /></Tooltip>}
        </Space>
      )
    },
    {
      title: 'Phone',
      dataIndex: 'phone',
    },
    {
      title: 'Source',
      dataIndex: 'source',
      render: src => <Badge count={src} style={{ backgroundColor: '#f5f5f5', color: '#595959', borderColor: '#d9d9d9' }} />
    },
    {
      title: 'Status',
      dataIndex: 'status',
      render: status => <Tag color={STATUS_COLORS[status] || 'default'}>{status?.toUpperCase()}</Tag>
    },
    {
      title: 'Interest',
      dataIndex: 'productInterest',
      render: text => text || '-'
    },
    {
      title: 'Last Activity',
      dataIndex: 'lastActivityAt',
      render: date => date ? new Date(date).toLocaleString() : '-'
    },
    {
      title: 'Action',
      render: (_, record) => (
        <Button type="link" size="small" onClick={() => openDrawer(record)}>View</Button>
      )
    }
  ];

  return (
    <div className="lead-management-page">
      <Space direction="vertical" style={{ width: '100%' }} size="large">


        {/* Header Stats */}
        <Row gutter={16}>
          <Col span={6}>
            <Card size="small">
              <Text type="secondary">Total Leads</Text>
              <Title level={3} style={{ margin: 0 }}>{stats.total}</Title>
            </Card>
          </Col>
          <Col span={6}>
            <Card size="small">
              <Text type="secondary">New</Text>
              <Title level={3} style={{ margin: 0, color: '#1890ff' }}>{stats.new}</Title>
            </Card>
          </Col>
          <Col span={6}>
            <Card size="small">
              <Text type="secondary">Contacted</Text>
              <Title level={3} style={{ margin: 0, color: '#faad14' }}>{stats.contacted}</Title>
            </Card>
          </Col>
          <Col span={6}>
            <Card size="small">
              <Text type="secondary">Converted</Text>
              <Title level={3} style={{ margin: 0, color: '#52c41a' }}>{stats.converted}</Title>
            </Card>
          </Col>
        </Row>

        <div className="lead-contact-grid">
          <section className="lead-table-panel">
            {/* Toolbar */}
            <div className="lead-toolbar">
              <Space wrap>
                <Input
                  placeholder="Search name/phone"
                  prefix={<SearchOutlined />}
                  onChange={e => handleFilterChange('search', e.target.value)}
                  style={{ width: 220 }}
                  allowClear
                />
                <Select
                  placeholder="Status"
                  onChange={v => handleFilterChange('status', v)}
                  style={{ width: 140 }}
                  allowClear
                >
                  <Option value="new">New</Option>
                  <Option value="contacted">Contacted</Option>
                  <Option value="qualified">Qualified</Option>
                  <Option value="converted">Converted</Option>
                  <Option value="churned">Churned</Option>
                </Select>
                <Select
                  placeholder="Source"
                  onChange={v => handleFilterChange('source', v)}
                  style={{ width: 140 }}
                  allowClear
                >
                  <Option value="whatsapp">WhatsApp</Option>
                  <Option value="shopify">Shopify</Option>
                  <Option value="csv">CSV</Option>
                  <Option value="manual">Manual</Option>
                </Select>
              </Space>
              <Space>
                <Button icon={<UploadOutlined />} onClick={() => setImportModalVisible(true)}>Import CSV</Button>
                <Button type="primary" icon={<PlusOutlined />} onClick={() => setAddModalVisible(true)}>Add Lead</Button>
              </Space>
            </div>

            {/* Table */}
            <Table
              columns={columns}
              dataSource={leads}
              rowKey="_id"
              loading={loading}
              pagination={{
                current: filters.page,
                pageSize: filters.limit,
                total: totalLeads
              }}
              onChange={handleTableChange}
            />
          </section>

        </div>
      </Space>

      {/* Detail Drawer */}
      <Drawer
        title="Lead Details"
        width={400}
        onClose={() => setDrawerVisible(false)}
        open={drawerVisible}
      >
        {selectedLead && (
          <Space direction="vertical" style={{ width: '100%' }} size="middle">
            <div>
              <Title level={4} style={{ margin: 0 }}>{selectedLead.name || 'Unknown'}</Title>
              <Text type="secondary">{selectedLead.phone} {selectedLead.email && ` | ${selectedLead.email}`}</Text>
            </div>

            <div>
              <Text strong>Status: </Text>
              <Select
                value={selectedLead.status}
                onChange={(v) => handleUpdateLead({ status: v })}
                style={{ width: 150 }}
              >
                <Option value="new">New</Option>
                <Option value="contacted">Contacted</Option>
                <Option value="qualified">Qualified</Option>
                <Option value="converted">Converted</Option>
                <Option value="churned">Churned</Option>
              </Select>
            </div>

            <Card size="small" title="Insights">
              <p><Text strong>Product Interest:</Text> {selectedLead.productInterest || '-'}</p>
              <p><Text strong>Source:</Text> <Badge count={selectedLead.source} style={{ backgroundColor: '#f5f5f5', color: '#595959' }} /></p>
            </Card>

            <div>
              <Text strong>Tags</Text>
              <Select
                mode="tags"
                style={{ width: '100%' }}
                value={selectedLead.tags || []}
                onChange={(v) => handleUpdateLead({ tags: v })}
                placeholder="Add tags..."
              />
            </div>

            <div>
              <Text strong>Notes</Text>
              <TextArea
                rows={4}
                value={selectedLead.notes}
                onChange={(e) => setSelectedLead({ ...selectedLead, notes: e.target.value })}
                onBlur={(e) => handleUpdateLead({ notes: e.target.value })}
                placeholder="Add notes..."
              />
            </div>

            {selectedLead.chatId && (
              <Button type="dashed" block icon={<MessageOutlined />} href="/#">
                View Conversation (ChatId: {selectedLead.chatId})
              </Button>
            )}
          </Space>
        )}
      </Drawer>

      {/* Add Lead Modal */}
      <Modal
        title="Add Lead"
        open={addModalVisible}
        onCancel={() => setAddModalVisible(false)}
        onOk={() => addForm.submit()}
      >
        <Form form={addForm} layout="vertical" onFinish={handleAddSubmit}>
          <Form.Item name="name" label="Name"><Input /></Form.Item>
          <Form.Item name="phone" label="Phone"><Input /></Form.Item>
          <Form.Item name="email" label="Email"><Input /></Form.Item>
          <Form.Item name="status" label="Status" initialValue="new">
            <Select>
              <Option value="new">New</Option>
              <Option value="contacted">Contacted</Option>
              <Option value="qualified">Qualified</Option>
            </Select>
          </Form.Item>
        </Form>
      </Modal>

      {/* Import CSV Modal */}
      <Modal
        title="Import Leads (CSV)"
        open={importModalVisible}
        onCancel={() => { setImportModalVisible(false); setImportFile(null); }}
        onOk={handleImport}
      >
        <p>Upload a CSV file containing leads. Expected columns: <b>name, phone, email, tags</b>.</p>
        <Upload
          beforeUpload={file => { setImportFile(file); return false; }}
          maxCount={1}
          accept=".csv"
        >
          <Button icon={<UploadOutlined />}>Select File</Button>
        </Upload>
        {importFile && <p style={{ marginTop: 10 }}>Selected: {importFile.name}</p>}
      </Modal>
    </div>
  );
};

export default LeadList;
